import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  input,
  output,
  signal,
  untracked,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatExpansionModule } from '@angular/material/expansion';
import { IconInduxtryComponent } from 'src/app/shared/components/common/icon-induxtry/icon-induxtry.component';
import { PageSelectComponent } from 'src/app/shared/components/common/page-select/page-select.component';
import { EmptyStateComponent } from 'src/app/shared/components/common/empty-state/empty-state.component';
import { SkeletonDirective } from 'src/app/shared/directives/skeleton/skeleton.directive';
import { PipelineLayersConsoleComponent } from '../pipeline-layers-console/pipeline-layers-console.component';
import { STATUS_ICONS, STATUS_LABELS, PipelineExecutionStatus } from '../../models/conexoes.model';
import {
  GetItemJobsFilters,
  JobStatusCounts,
  JobsStatusFilter,
  Layer,
} from '../../services/detail-connected-item.service';

export type PipelineStageStatus = 'success' | 'failure' | 'skipped' | 'pending' | 'processing';

export interface PipelineStage {
  name: string;
  status: PipelineStageStatus;
}

export interface PipelineRun {
  id: string;
  runNumber: string;
  description: string;
  status: PipelineExecutionStatus;
  automated?: boolean;
  createdAt?: string;
  layers?: Layer[];
}

export interface PipelineRunsPageChange {
  pageIndex: number;
  pageSize: number;
}

export type PipelineRunsStatusFilter = JobsStatusFilter | 'all';
export type PipelineRunsTypeFilter = 'all' | 'automated';

const STATUS_FILTER_ALL = 'all';
const TYPE_FILTER_ALL = 'all';

export interface PipelineStatusOption {
  label: string;
  value: PipelineRunsStatusFilter;
  dot?: 'success' | 'failure' | 'in_progress';
}

export interface PipelineTypeOption {
  label: string;
  value: PipelineRunsTypeFilter;
}

@Component({
  selector: 'app-pipeline-runs',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatButtonToggleModule,
    MatExpansionModule,
    PageSelectComponent,
    IconInduxtryComponent,
    SkeletonDirective,
    PipelineLayersConsoleComponent,
    EmptyStateComponent,
  ],
  templateUrl: './pipeline-runs.component.html',
  styleUrls: ['./pipeline-runs.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PipelineRunsComponent {
  readonly pageSizeOptions = [15, 30, 50];

  readonly jobId = input<string | null>(null);
  readonly runsInput = input<PipelineRun[]>([]);
  readonly selectedRunId = input<string | null>(null);
  readonly expandedRunId = input<string | null>(null);
  readonly initialSelectedLayerId = input<string | null>(null);
  readonly totalCount = input(0);
  readonly page = input(1);
  readonly pageSize = input(15);
  readonly isLoading = input(false);
  readonly filters = input<GetItemJobsFilters>({});
  readonly statusCounts = input<JobStatusCounts>({
    all: 0,
    completed: 0,
    failure: 0,
    in_progress: 0,
  });
  readonly viewExecution = output<PipelineRun>();
  readonly pageChange = output<PipelineRunsPageChange>();
  readonly filtersChange = output<GetItemJobsFilters>();
  /** Camadas do job selecionado (fonte única no container) — evita GET /layers duplicado. */
  readonly selectedJobLayers = input<Layer[]>([]);
  readonly layersLoading = input(false);
  readonly allRuns = signal<PipelineRun[]>([]);
  readonly runs = computed<PipelineRun[]>(() => this.allRuns() ?? []);
  readonly hasRuns = computed(() => (this.runs().length ?? 0) > 0);
  /** Painel aberto pelo usuário (ou por "acompanhar logs"). No máximo um por vez. */
  readonly openedRunId = signal<string | null>(null);

  readonly statusOptions: PipelineStatusOption[] = [
    { label: 'Todas', value: STATUS_FILTER_ALL },
    { label: 'Sucesso', value: 'completed', dot: 'success' },
    { label: 'Falha', value: 'failure', dot: 'failure' },
    { label: 'Em andamento', value: 'in_progress', dot: 'in_progress' },
  ];

  readonly typeOptions: PipelineTypeOption[] = [
    { label: 'Todas', value: TYPE_FILTER_ALL },
    { label: 'Somente automáticas', value: 'automated' },
  ];

  readonly statusFilter = computed<PipelineRunsStatusFilter>(
    () => this.filters().status ?? STATUS_FILTER_ALL
  );
  readonly typeFilter = computed<PipelineRunsTypeFilter>(
    () => (this.filters().automated ? 'automated' : TYPE_FILTER_ALL)
  );

  readonly hasActiveFilters = computed(
    () => this.statusFilter() !== STATUS_FILTER_ALL || this.typeFilter() !== TYPE_FILTER_ALL
  );

  readonly statusCountByValue = computed(() => {
    const counts = this.statusCounts();
    return {
      all: counts.all,
      completed: counts.completed,
      failure: counts.failure,
      in_progress: counts.in_progress,
    } as Record<PipelineRunsStatusFilter, number>;
  });

  readonly statusIconMap = STATUS_ICONS;
  readonly statusLabelMap = STATUS_LABELS;
  readonly automatedExecutionLabel = 'Execução automática';

  constructor() {
    effect(() => {
      const incomingRuns = this.runsInput();
      untracked(() => {
        const currentById = new Map(this.allRuns().map((run) => [run.id, run]));
        const mergedRuns = incomingRuns.map((run) => {
          const existingRun = currentById.get(run.id);
          const hasIncomingLayers = (run.layers?.length ?? 0) > 0;
          if (hasIncomingLayers || !existingRun?.layers) return run;
          return { ...run, layers: existingRun.layers };
        });
        this.allRuns.set(mergedRuns);
      });
    });

    effect(() => {
      const selectedId = this.selectedRunId();
      const layers = this.selectedJobLayers();
      if (!selectedId || !layers?.length) return;
      untracked(() => {
        this.allRuns.update((runs) =>
          runs.map((run) =>
            run.id === selectedId ? { ...run, layers } : run
          )
        );
      });
    });

    effect(() => {
      const expandedId = this.expandedRunId();
      if (!expandedId) return;
      untracked(() => this.openedRunId.set(expandedId));
    });
  }

  isRunOpened(runId: string): boolean {
    return this.openedRunId() === runId;
  }

  onPanelOpened(runId: string): void {
    this.openedRunId.set(runId);
  }

  onPanelClosed(runId: string): void {
    if (this.openedRunId() === runId) {
      this.openedRunId.set(null);
    }
  }

  statusOptionCount(value: PipelineRunsStatusFilter): number {
    return this.statusCountByValue()[value] ?? 0;
  }

  isStatusOptionDisabled(value: PipelineRunsStatusFilter): boolean {
    if (value === STATUS_FILTER_ALL) return false;
    return this.statusOptionCount(value) === 0;
  }

  onStatusFilterChange(value: PipelineRunsStatusFilter): void {
    if (value === this.statusFilter() || this.isStatusOptionDisabled(value)) return;
    this.emitFilters({ status: value });
  }

  onTypeFilterChange(value: PipelineRunsTypeFilter): void {
    if (value === this.typeFilter()) return;
    this.emitFilters({ automated: value === 'automated' });
  }

  clearFilters(): void {
    if (!this.hasActiveFilters()) return;
    this.filtersChange.emit({});
  }

  private emitFilters(patch: {
    status?: PipelineRunsStatusFilter;
    automated?: boolean;
  }): void {
    const status = patch.status ?? this.statusFilter();
    const automated = patch.automated ?? (this.typeFilter() === 'automated');
    const filters: GetItemJobsFilters = {};

    if (status !== STATUS_FILTER_ALL) {
      filters.status = status;
    }
    if (automated) {
      filters.automated = true;
    }

    this.filtersChange.emit(filters);
  }

  onViewExecution(run: PipelineRun): void {
    this.viewExecution.emit(run);
  }

  onHeaderClick(run: PipelineRun): void {
    if (run.id !== this.selectedRunId()) {
      this.onViewExecution(run);
    }
  }

  onPageSelected(page: number): void {
    const pageNumber = Number(page);
    if (!Number.isFinite(pageNumber) || pageNumber < 1 || pageNumber === this.page()) return;

    this.pageChange.emit({
      pageIndex: pageNumber - 1,
      pageSize: this.pageSize(),
    });
  }

  onPageSizeChange(pageSize: number): void {
    if (pageSize === this.pageSize()) return;

    this.pageChange.emit({
      pageIndex: 0,
      pageSize,
    });
  }
}
